import axios from 'axios'

import { saveCheckoutMetadata } from '../checkoutMetadata'
import { getPmsApiClient } from './pmsApiClient'
import {
	type PmsProductWithAllPrices,
	type PmsProductWithOnePrice,
	type PmsSubscription,
	PmsSubscriptionStatusEnum,
	type PmsUpsellProduct,
} from './pmsTypes'

export const pmsApi = {
	// Location-sensitive: the PMS uses Cloudflare's geo headers to resolve the
	// default currency/provider account. Call this client-side, or forward
	// those headers explicitly if it ever runs server-side. Only for users who
	// were never subscribed — use getUserProducts for resubscribing users.
	// `pricingCampaign` overrides PMS's configured campaign; an unknown name
	// yields an empty list, not an error.
	async getProducts(
		pricingCampaign?: string,
	): Promise<PmsProductWithAllPrices[]> {
		const { data } = await getPmsApiClient().get<PmsProductWithAllPrices[]>(
			'/products',
			{ params: pricingCampaign ? { pricingCampaign } : undefined },
		)

		// TEMP (2026-08-10): prod's /prices excludes monthly_1 for guests —
		// its qr-api query is scoped to the GUEST user status, and monthly_1
		// may not be associated with that status there. Filtering it out
		// here until confirmed with Payments whether PMS's migrated data
		// carried that same status association.
		const withoutMonthly1 = data.filter(
			(product) =>
				product.metadata?.nameTranslationKey !==
				'product.monthly_1_price_increased.name',
		)

		// Descending, matching qr-api's actual query (confirmed in
		// products.service.ts: `.orderBy('p.priority', 'DESC')`) — the API
		// doesn't guarantee response order matches it.
		return [...withoutMonthly1].sort(
			(a, b) => (b.priority ?? 0) - (a.priority ?? 0),
		)
	},

	// For users who can resubscribe to a plan they already had.
	async getUserProducts(): Promise<PmsProductWithOnePrice[]> {
		const { data } =
			await getPmsApiClient().get<PmsProductWithOnePrice[]>(
				'/products/user',
			)

		return data
	},

	async getUpsellProducts(): Promise<PmsUpsellProduct[]> {
		const { data } =
			await getPmsApiClient().get<PmsUpsellProduct[]>('/products/upsell')

		return data
	},

	async buyUpsell(
		priceId: string,
	): Promise<{ success: boolean; clientSecret: string }> {
		const { data } = await getPmsApiClient().post<{
			success: boolean
			clientSecret: string
		}>('/products/upsell/buy', { priceId })

		return data
	},

	// Returns null when the user never had a subscription (backend responds 404).
	async getUserSubscription(): Promise<PmsSubscription | null> {
		try {
			const { data } =
				await getPmsApiClient().get<PmsSubscription>('/subscriptions')

			return data
		} catch (error) {
			if (axios.isAxiosError(error) && error.response?.status === 404) {
				return null
			}

			throw error
		}
	},

	async createStripeSubscription(payload: {
		priceId: string
		paymentMethodId: string
		trackingMetadata?: Record<string, unknown>
		zipCode?: string
	}): Promise<{
		status: PmsSubscriptionStatusEnum
		clientSecret: string
		paymentIntentId?: string
	}> {
		await saveCheckoutMetadata()

		const { data } = await getPmsApiClient().post<{
			status: PmsSubscriptionStatusEnum
			clientSecret: string
			paymentIntentId?: string
		}>('/subscriptions/stripe', payload)

		return data
	},

	// paymentMethodId is optional — omit it to retry payment on the card
	// already on file instead of switching to a new one.
	async updateStripeSubscription(paymentMethodId?: string): Promise<{
		status: PmsSubscriptionStatusEnum
		clientSecret: string
		paymentIntentId?: string
	}> {
		const { data } = await getPmsApiClient().post<{
			status: PmsSubscriptionStatusEnum
			clientSecret: string
			paymentIntentId?: string
		}>(
			'/subscriptions/stripe/payment-method',
			paymentMethodId ? { paymentMethodId } : {},
		)

		return data
	},

	async syncStripeSubscription(): Promise<{
		status: PmsSubscriptionStatusEnum
	}> {
		const { data } = await getPmsApiClient().post<{
			status: PmsSubscriptionStatusEnum
		}>('/subscriptions/stripe/sync')

		return data
	},

	async createNmiSubscription(payload: {
		priceId: string
		paymentToken: string
		trackingMetadata?: Record<string, unknown>
	}): Promise<{ status: PmsSubscriptionStatusEnum; transactionId?: string }> {
		const { data } = await getPmsApiClient().post<{
			status: PmsSubscriptionStatusEnum
			transactionId?: string
		}>('/subscriptions/nmi', payload)

		return data
	},

	async createPaypalSubscription(payload: {
		priceId: string
		subscriptionId: string
	}): Promise<{ status: string; approveLink: string }> {
		await saveCheckoutMetadata()

		const { data } = await getPmsApiClient().post<{
			status: string
			approveLink: string
		}>('/subscriptions/paypal', payload)

		return data
	},

	async syncPaypalSubscription(payload: {
		priceId: string
		subscriptionId: string
	}): Promise<{ status: string; approveLink: string }> {
		await saveCheckoutMetadata()

		const { data } = await getPmsApiClient().post<{
			status: string
			approveLink: string
		}>('/subscriptions/paypal/sync', payload)

		return data
	},

	async cancelUserSubscription(
		cancellationReason?: string,
	): Promise<{ message: string }> {
		const { data } = await getPmsApiClient().post<{ message: string }>(
			'/subscriptions/cancel',
			cancellationReason ? { cancellationReason } : {},
		)

		return data
	},

	async reactivateUserSubscription(): Promise<{ message: string }> {
		const { data } = await getPmsApiClient().post<{ message: string }>(
			'/subscriptions/reactivate',
		)

		return data
	},
}
